import { useState, useEffect } from 'react';
import { Modal, Grid, Button, Divider, Select, Title, Box, Text } from '@mantine/core';
import { IconPrinter, IconPhotoOff } from '@tabler/icons-react';

import * as actions from '../../../actions/Lote';
import { urlApi } from '../../../utils';
import Viewer from '../../../components/SmartForm/Viewer';
import SmartMap, { mapKey } from '../../../components/Map/SmartMapFallback';
import { Skeleton } from '@mantine/core';
import { DetailSkeleton } from '../../../components/TableSkeleton';
import EmptyState from '../../../components/EmptyState';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { Unidade } from '../../../store/types';

import {
  relatorioModalClassNames,
  RELATORIO_GRID_BREAKPOINTS,
  RELATORIO_MODAL_PADDING,
  RELATORIO_MODAL_SIZE,
  indiceFotoMaisRecente,
} from './relatorioUnidadePrint';

interface RelatorioUnidadeProps {
  unidadeId: number;
  onClose: () => void;
}

export default function RelatorioUnidade({ unidadeId, onClose }: RelatorioUnidadeProps) {
  const dispatch = useAppDispatch();
  const current = useAppSelector((state) => state.lote.current);
  const photos = useAppSelector((state) => state.lote.photos);
  const config = useAppSelector((state) => state.container.config);

  const [unidade, setUnidade] = useState<Unidade | null>(null);
  const [loadingUnidade, setLoadingUnidade] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingFoto, setLoadingFoto] = useState(true);
  const [fotoSelecionada, setFotoSelecionada] = useState<string | null>('');

  useEffect(() => {
    if (!unidadeId) return;
    setLoadingUnidade(true);
    setLoadFailed(false);
    dispatch((actions as any).getRelatorioUnidade(
      unidadeId,
      ({ data }: { data: Unidade }) => { setUnidade(data); setLoadingUnidade(false); },
      () => { setLoadFailed(true); setLoadingUnidade(false); },
    ));
  }, [unidadeId, dispatch]);

  useEffect(() => {
    if (photos?.thumbs500 && photos.thumbs500.length > 0) {
      setFotoSelecionada(photos.thumbs500[indiceFotoMaisRecente(photos.dates, photos.thumbs500.length)]);
    }
  }, [photos]);

  if (!unidade) {
    return (
      <Modal opened onClose={onClose} size="90%" title="Relatório da unidade">
        {loadingUnidade ? (
          <DetailSkeleton lines={10} />
        ) : loadFailed ? (
          <EmptyState label="Não foi possível carregar o relatório da unidade" mih={240} />
        ) : null}
      </Modal>
    );
  }

  const temFoto = !!photos?.thumbs500 && photos.thumbs500.length > 0;

  return (
    <Modal
      opened
      onClose={onClose}
      withCloseButton
      closeOnClickOutside={false}
      size={RELATORIO_MODAL_SIZE}
      classNames={relatorioModalClassNames}
      padding={RELATORIO_MODAL_PADDING}
      title={
        <Button leftSection={<IconPrinter size={16} />} onClick={() => window.print()}>
          Imprimir
        </Button>
      }
    >
      <Grid align="center" justify="center">
        <Grid.Col span={3}>
          <img src={urlApi('PublicConfig/DownloadClientLogoMedium')} alt="logo-medium" width="150" />
        </Grid.Col>
        <Grid.Col span={9} style={{ textAlign: 'center' }}>
          <Title order={2} mb={2}>{config?.clientName}</Title>
          <Title order={1} mb={2}>Relatório de Unidade</Title>
          <Text size="md"><Text component="span" fw={700}>Inscrição: </Text>{unidade.inscricao}</Text>
        </Grid.Col>
      </Grid>

      <Box className="relatorio-unidade__mapa" mt="md">
        <SmartMap
          mapKey={mapKey.GEOFINANCEIRO_REL_UNIDADE_PUBLIC}
          geoJson={current ? current.geoJson : null}
          destaqueSelecao
        />
      </Box>

      <Grid gap="md" mt="md" type="container" breakpoints={RELATORIO_GRID_BREAKPOINTS}>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Divider label="Foto" labelPosition="left" my="md" />
          {!temFoto && <EmptyState label="Não há fotos" icon={IconPhotoOff} mih={160} />}
          {temFoto && photos?.thumbs500 && (
            <Box>
              <Select
                mb="sm"
                className="do-not-print"
                value={fotoSelecionada}
                onChange={(v) => { setFotoSelecionada(v); setLoadingFoto(true); }}
                data={photos.thumbs500.map((x, index) => ({
                  value: x,
                  label: `Foto ${index + 1}/${photos.thumbs500!.length} - ${photos.dates?.[index] ?? ''}`,
                }))}
              />
              {loadingFoto && <Skeleton height={183} radius="md" />}
              {fotoSelecionada && (
                <img
                  src={fotoSelecionada}
                  onLoad={() => setLoadingFoto(false)}
                  alt="Foto da fachada"
                  className="relatorio-unidade__foto"
                  style={{ maxWidth: '100%', height: 'auto', display: loadingFoto ? 'none' : 'block' }}
                />
              )}
            </Box>
          )}
          <Viewer type="tables" fieldItemGroups={unidade.unidadeFieldItemGroups} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Viewer type="tables" fieldItemGroups={unidade.loteFieldItemGroups} />
        </Grid.Col>
      </Grid>
    </Modal>
  );
}
